import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { BusinessAddressTaxForm } from './index';
import { BusinessName } from './business-name';
import { PhoneNumber } from './phone-number';
import { TimeZone } from './time-zone';
import { TaxCertificationPropTypes, TaxDataPropTypes } from './constants';

const { CountryCode } = Constants;

/**
 * this.state
 * @property {Array} countries - list of countries for dropdown
 * @property {Array} states - list of states for dropdown
 * @property {Array} timeZones - list of timeZones for dropdown
 * @property {AddressField} Line1, Line2, City, StateOrProvince, PostalCode, Country object
 * @property {string} BusinessName, PhoneNumber, TimeZone object
*/

export class SignupAddressTaxForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      BusinessName: {
        value: props.businessName,
        isValid: true,
      },
      PhoneNumber: {
        value: props.phoneNumber,
        isValid: true,
      },
      TimeZone: props.timeZone,
      address: props.address,
      taxData: props.taxData,
      taxCertification: props.taxCertification,
      timeZones: _.map(this.props.timeZones, timeZone => ({
        id: timeZone.Value,
        value: timeZone.Text,
      })),
      timeZonesPerCountryMapping: props.timeZonesPerCountryMapping,
      isBusinessNameValid: true,
      isPhoneNumberValid: true,
      isAddressValid: true,
      isTaxValid: true,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(prevState, this.state)) {
      this.props.onFormValidate({
        businessName: this.state.BusinessName.value,
        phoneNumber: this.state.PhoneNumber.value,
        timeZone: this.state.TimeZone,
        address: this.state.address,
        taxInfo: this.state.taxData,
        taxCertification: this.state.taxCertification,
        isValid: this.state.isAddressValid && this.state.isTaxValid && this.state.BusinessName.isValid && this.state.PhoneNumber.isValid,
      });
    }
  }

  onTimeZoneChange = (timeZone) => {
    this.setState({ TimeZone: timeZone });
  };

  onAddressTaxFormChange = (state) => {
    if (state && state.address) {
      this.setState({ address: state.address, isAddressValid: state.isAddressValid });
    }

    if (state && state.taxData) {
      this.setState({ taxData: state.taxData, taxCertification: state.taxCertification, isTaxValid: state.isTaxValid });
    }
  };

  onBusinessNameChange = (businessNameModel) => {
    this.setState({
      BusinessName: {
        value: businessNameModel.businessName,
        isValid: businessNameModel.isValid,
      },
    });
  };

  onPhoneNumberChange = (phoneNumberModel) => {
    this.setState({
      PhoneNumber: {
        value: phoneNumberModel.phoneNumberWithCallingCode,
        isValid: phoneNumberModel.isValid,
      },
    });
  };

  render() {
    const {
      id,
      i18n,
      countries,
      findCountries,
      findStates,
      businessName,
      phoneNumber,
      timeZone,
      timeZones,
      timeZonesPerCountryMapping,
      isABLColombiaTaxEnabled,
      alwaysShowError,
      ownerAccountId,
      urlBuilder,
      showTaxCertLink,
      permissions,
      vatStatus,
    } = this.props;

    return (
      <div className="signup-address-tax-form" id={id}>
        <BusinessName
          i18n={i18n}
          id={id}
          businessName={businessName}
          onBusinessNameChange={this.onBusinessNameChange}
          helpPopupTopic={this.props.businessNamePopupId}
          alwaysShowError={alwaysShowError}
          showLineByLineFormGroup
        />

        <PhoneNumber
          i18n={i18n}
          id={id}
          countries={countries}
          phoneNumber={phoneNumber}
          parseCallingCode
          onPhoneNumberChange={this.onPhoneNumberChange}
          initialCountryCode={this.props.address ? this.props.address.Country : CountryCode.US}
          alwaysShowError={alwaysShowError}
          showCountryCallingCode
        />

        <BusinessAddressTaxForm
          id={id}
          i18n={i18n}
          countries={countries}
          findCountries={findCountries}
          findStates={findStates}
          onAddressTaxFormChange={this.onAddressTaxFormChange}
          address={this.props.address}
          taxData={this.props.taxData}
          mtTaxData={this.props.mtTaxData}
          taxCertification={this.props.taxCertification}
          mtTaxCertification={this.props.mtTaxCertification}
          helpPopupId={this.props.businessLocationPopupId}
          helpPopupAriaLabel={i18n.getString(_TL_('Business location tooltip'))}
          isABLColombiaTaxEnabled={isABLColombiaTaxEnabled}
          alwaysShowError={alwaysShowError}
          ownerAccountId={ownerAccountId}
          urlBuilder={urlBuilder}
          showTaxCertLink={showTaxCertLink}
          permissions={permissions}
          vatStatus={vatStatus}
          showSectionLabel
          useDropdownList
          isSignupFlow
        />
        <TimeZone
          i18n={i18n}
          id={id}
          timeZone={timeZone}
          countryCode={this.state.address.Country}
          onTimeZoneChange={this.onTimeZoneChange}
          timeZones={timeZones}
          timeZonesPerCountryMapping={timeZonesPerCountryMapping}
          helpPopupId={this.props.timeZonePopupId}
          helpPopupAriaLabel={i18n.getString(_TL_('Preferred time zone tooltip'))}
        />
      </div>
    );
  }
}

SignupAddressTaxForm.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  id: PropTypes.string.isRequired,
  businessName: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string,
  timeZone: PropTypes.string,
  findCountries: PropTypes.func.isRequired,
  findStates: PropTypes.func.isRequired,
  address: PropTypes.shape({
    Line1: PropTypes.string,
    Line2: PropTypes.string,
    City: PropTypes.string,
    StateOrProvince: PropTypes.string,
    PostalCode: PropTypes.string,
    Country: PropTypes.string,
  }),
  taxData: TaxDataPropTypes,
  mtTaxData: TaxDataPropTypes,
  taxCertification: TaxCertificationPropTypes,
  mtTaxCertification: TaxCertificationPropTypes,
  onFormValidate: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Code: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
  })).isRequired,
  timeZones: PropTypes.arrayOf(PropTypes.shape({
    Value: PropTypes.string.isRequired,
    Text: PropTypes.string.isRequired,
  })).isRequired,
  timeZonesPerCountryMapping: PropTypes.arrayOf(PropTypes.shape({
    country: PropTypes.string.isRequired,
    timeZones: PropTypes.arrayOf(PropTypes.number.isRequired),
  })),
  businessLocationPopupId: PropTypes.string,
  businessNamePopupId: PropTypes.string,
  timeZonePopupId: PropTypes.string,
  alwaysShowError: PropTypes.bool,
  isABLColombiaTaxEnabled: PropTypes.bool,
  ownerAccountId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  urlBuilder: PropTypes.shape({
    UrlBuilders: PropTypes.shape({}),
    UrlHelper: PropTypes.shape({}),
  }),
  showTaxCertLink: PropTypes.bool,
  permissions: PropTypes.shape({
    dynamic: PropTypes.objectOf(PropTypes.bool),
  }),
  vatStatus: PropTypes.number,
};

SignupAddressTaxForm.defaultProps = {
  phoneNumber: '',
  timeZone: '0',
  address: {},
  taxData: [],
  mtTaxData: [],
  taxCertification: null,
  mtTaxCertification: null,
  timeZonesPerCountryMapping: [],
  businessLocationPopupId: '',
  businessNamePopupId: '',
  timeZonePopupId: '',
  alwaysShowError: false,
  isABLColombiaTaxEnabled: false,
  ownerAccountId: null,
  urlBuilder: {
    UrlBuilders: {},
    UrlHelper: {},
  },
  showTaxCertLink: false,
  permissions: {
    dynamic: {},
  },
  vatStatus: null,
};
